/**
 * Responsive exploration zoom for continuous presence spaces.
 *
 * World layer CSS size is `worldWidth × playfield` / `worldHeight × playfield`
 * (or aspect-locked height from width). Framing into the playfield:
 *
 *   fitZoom   = min(1 / worldWidth, 1 / worldHeight) × (1 − padding)  // contain
 *   coverZoom = max(1 / worldWidth, 1 / worldHeight) × (1 − padding)  // cover
 *
 * Desktop uses fit (full yard visible). Portrait phones use cover (viewport
 * filled, yard cropped, camera pans). Landscape phones use near-cover zoom
 * (full width) with pan locked and a slight zoom-out + downward framing bias
 * so the patio stays above Safari’s toolbar. Tablets use balanced.
 */

export type ExplorationCameraMode = 'fit' | 'balanced' | 'follow';

export interface ResponsiveExploreConfig {
  /** Min playfield width (px) to use full-yard fit zoom. */
  desktopFitBreakpoint: number;
  /** Min playfield height (px) paired with desktopFitBreakpoint. */
  desktopFitMinHeight: number;
  /** Min playfield width (px) for balanced / iPad-style framing. */
  tabletBreakpoint: number;
  /**
   * Tablet landscape may reuse the Desktop Backyard plate when both edges
   * clear these floors (large iPad landscape). Compact tablets use Mobile.
   */
  tabletDesktopMinWidth: number;
  tabletDesktopMinHeight: number;
  /**
   * Large ~4:3 playfields below this width stay Tablet (map important-contain)
   * even when they clear desktopFitBreakpoint — e.g. iPad Pro landscape.
   */
  largeTabletMaxWidth: number;
  /** Aspect below this (w/h) counts as ~4:3 tablet vs ultrawide desktop. */
  largeTabletMaxAspect: number;
  /**
   * Landscape-phone height ceiling (px) for backyard presentation mode.
   * Short desktop windows (tall width, short height) must NOT match this alone.
   */
  landscapePhoneMaxHeight: number;
  /**
   * Landscape-phone width ceiling (px). Paired with landscapePhoneMaxHeight so
   * only phone-sized landscape viewports select the Mobile Backyard plate.
   */
  landscapePhoneMaxWidth: number;
  /** Fraction of the playfield kept as empty margin around the fitted world (0–0.25). */
  desktopFitPadding: number;
  /** Slightly less padding on tablet when showing as much yard as practical. */
  tabletFitPadding: number;
  /** Cover zoom uses tiny/no padding so the viewport stays filled. */
  mobileCoverPadding: number;
  /**
   * Landscape phones: multiply cover zoom by this (<1) to zoom out slightly
   * so patio / clothesline clear the browser toolbar. Keeps near-full width.
   */
  landscapeCoverRelax: number;
  /**
   * Landscape phones: shift locked frame center downward in world Y so more
   * of the patio is visible (camera content moves up on screen).
   */
  landscapePatioBiasY: number;
  /** Floor for exploration zoom (character must stay readable). */
  minimumExplorationZoom: number;
  /**
   * Soft ceiling for balanced / legacy crop feel. Follow/cover mode is not
   * clamped to this — cover must exceed it on tall phones.
   */
  maximumExplorationZoom: number;
  /** Safety ceiling for cover zoom on extreme aspect ratios. */
  maximumCoverZoom: number;
  /** Relative lean-in when focusing an interactive object. */
  focusZoomRelative: number;
}

export const DEFAULT_RESPONSIVE_EXPLORE: Readonly<ResponsiveExploreConfig> = {
  desktopFitBreakpoint: 1200,
  desktopFitMinHeight: 700,
  tabletBreakpoint: 768,
  tabletDesktopMinWidth: 1100,
  tabletDesktopMinHeight: 750,
  largeTabletMaxWidth: 1400,
  largeTabletMaxAspect: 1.45,
  landscapePhoneMaxHeight: 500,
  landscapePhoneMaxWidth: 1000,
  desktopFitPadding: 0.06,
  tabletFitPadding: 0.04,
  mobileCoverPadding: 0,
  landscapeCoverRelax: 0.9,
  landscapePatioBiasY: 0.06,
  minimumExplorationZoom: 0.48,
  maximumExplorationZoom: 0.84,
  maximumCoverZoom: 4.5,
  focusZoomRelative: 1.26,
};

export interface ExploreZoomInput {
  playfieldWidth: number;
  playfieldHeight: number;
  worldWidth: number;
  worldHeight: number;
  config?: Partial<ResponsiveExploreConfig>;
}

export interface ExploreZoomResult {
  /** Scale applied to the world layer while exploring. */
  zoom: number;
  /** How framing should behave at this zoom. */
  mode: ExplorationCameraMode;
  /** Raw fit before clamps / mode policy. */
  fitZoom: number;
  /** Raw cover before clamps. */
  coverZoom: number;
  /** focusZoom = zoom × focusZoomRelative */
  focusZoom: number;
  /**
   * Locked camera center for landscape phones (patio bias). Null for
   * desktop / portrait / tablet where framing follows the usual policy.
   */
  frameCenter: { x: number; y: number } | null;
  config: ResponsiveExploreConfig;
}

export function resolveExploreZoom(input: ExploreZoomInput): ExploreZoomResult {
  const config: ResponsiveExploreConfig = {
    ...DEFAULT_RESPONSIVE_EXPLORE,
    ...input.config,
  };

  const padDesktop = clamp(config.desktopFitPadding, 0, 0.25);
  const padTablet = clamp(config.tabletFitPadding, 0, 0.25);
  const padCover = clamp(config.mobileCoverPadding, 0, 0.25);

  const fitZoomDesktop = computeFitZoom(
    input.worldWidth,
    input.worldHeight,
    padDesktop,
  );
  const fitZoomTablet = computeFitZoom(
    input.worldWidth,
    input.worldHeight,
    padTablet,
  );
  const coverZoom = computeCoverZoom(
    input.worldWidth,
    input.worldHeight,
    padCover,
  );

  const w = input.playfieldWidth;
  const h = input.playfieldHeight;
  const shortest = Math.min(w, h);
  const isDesktop =
    w >= config.desktopFitBreakpoint && h >= config.desktopFitMinHeight;
  // Phones: narrow portrait OR short landscape — not a single device ratio.
  const isPhone =
    !isDesktop &&
    (w < config.tabletBreakpoint || shortest < 500);
  const isTablet = !isDesktop && !isPhone;
  const isLandscapePhone = isPhone && w > h;

  let mode: ExplorationCameraMode;
  let zoom: number;
  let frameCenter: { x: number; y: number } | null = null;

  if (isDesktop) {
    mode = 'fit';
    zoom = clamp(
      fitZoomDesktop,
      config.minimumExplorationZoom,
      config.maximumExplorationZoom,
    );
  } else if (isTablet) {
    mode = 'balanced';
    // Show as much yard as practical, but never zoom in past the phone crop ceiling.
    // Narrow/short phones use full cover instead.
    const practical = Math.max(
      fitZoomTablet,
      config.maximumExplorationZoom * 0.9,
    );
    zoom = clamp(
      Math.min(practical, config.maximumExplorationZoom),
      config.minimumExplorationZoom,
      config.maximumExplorationZoom,
    );
  } else if (isLandscapePhone) {
    // Landscape phone: near-cover keeps full-width framing; slight zoom-out +
    // patio bias reveals the bottom of the yard above the browser toolbar.
    // Pan stays locked (no vertical scrubbing).
    mode = 'fit';
    const relax = clamp(config.landscapeCoverRelax, 0.75, 1);
    zoom = clamp(
      coverZoom * relax,
      config.minimumExplorationZoom,
      config.maximumCoverZoom,
    );
    frameCenter = {
      x: 0.5,
      y: clamp(0.5 + config.landscapePatioBiasY, 0.5, 0.72),
    };
  } else {
    // Portrait phone: cover the playfield — crop the yard; never letterbox.
    mode = 'follow';
    zoom = clamp(
      coverZoom,
      config.minimumExplorationZoom,
      config.maximumCoverZoom,
    );
  }

  return {
    zoom,
    mode,
    fitZoom: fitZoomDesktop,
    coverZoom,
    focusZoom: zoom * config.focusZoomRelative,
    frameCenter,
    config,
  };
}

/**
 * Cover-crop fractions for an image fitted with CSS `object-fit: cover`
 * into a viewport, with the crop window biased toward a focus point (0–1).
 */
export function coverCropInsetsFocused(
  imageAspect: number,
  viewportAspect: number,
  focusX = 0.5,
  focusY = 0.5,
): { left: number; right: number; top: number; bottom: number } {
  const img = Math.max(0.01, imageAspect);
  const view = Math.max(0.01, viewportAspect);
  const fx = Math.min(1, Math.max(0, focusX));
  const fy = Math.min(1, Math.max(0, focusY));
  if (img > view) {
    const visible = view / img;
    const maxLeft = 1 - visible;
    const left = Math.min(maxLeft, Math.max(0, fx - visible / 2));
    return { left, right: 1 - visible - left, top: 0, bottom: 0 };
  }
  const visible = img / view;
  const maxTop = 1 - visible;
  const top = Math.min(maxTop, Math.max(0, fy - visible / 2));
  return { left: 0, right: 0, top, bottom: 1 - visible - top };
}

/**
 * Cover-crop fractions for an image fitted with CSS `background-size: cover`
 * into a viewport (centered). Returns how much of the source image is clipped
 * on each edge (0–0.5).
 */
export function coverCropInsets(
  imageAspect: number,
  viewportAspect: number,
): { left: number; right: number; top: number; bottom: number } {
  return coverCropInsetsFocused(imageAspect, viewportAspect, 0.5, 0.5);
}

/**
 * Map a point in cover-fitted image space (0–1 of the master plate) into
 * viewport space (0–1 of the element showing `object-fit: cover`).
 * Returns null when the point is fully cropped away.
 */
export function coverImagePointToViewport(
  imageX: number,
  imageY: number,
  imageAspect: number,
  viewportAspect: number,
  focusX = 0.5,
  focusY = 0.5,
): { x: number; y: number } | null {
  const insets = coverCropInsetsFocused(
    imageAspect,
    viewportAspect,
    focusX,
    focusY,
  );
  const visibleW = 1 - insets.left - insets.right;
  const visibleH = 1 - insets.top - insets.bottom;
  if (visibleW <= 0 || visibleH <= 0) return null;
  if (
    imageX < insets.left ||
    imageX > 1 - insets.right ||
    imageY < insets.top ||
    imageY > 1 - insets.bottom
  ) {
    return null;
  }
  return {
    x: (imageX - insets.left) / visibleW,
    y: (imageY - insets.top) / visibleH,
  };
}

/**
 * Map a size in cover-fitted image space into viewport fractions.
 */
export function coverImageSizeToViewport(
  imageWidth: number,
  imageHeight: number,
  imageAspect: number,
  viewportAspect: number,
  focusX = 0.5,
  focusY = 0.5,
): { width: number; height: number } {
  const insets = coverCropInsetsFocused(
    imageAspect,
    viewportAspect,
    focusX,
    focusY,
  );
  const visibleW = Math.max(0.01, 1 - insets.left - insets.right);
  const visibleH = Math.max(0.01, 1 - insets.top - insets.bottom);
  return {
    width: imageWidth / visibleW,
    height: imageHeight / visibleH,
  };
}

/**
 * Letterbox insets for an image fitted with CSS `background-size: contain`
 * into a viewport (centered). Returns empty viewport bands on each edge (0–0.5).
 */
export function containLetterboxInsets(
  imageAspect: number,
  viewportAspect: number,
): { left: number; right: number; top: number; bottom: number } {
  const img = Math.max(0.01, imageAspect);
  const view = Math.max(0.01, viewportAspect);
  if (img > view) {
    // Image wider than viewport → bars on top/bottom.
    const used = view / img;
    const end = (1 - used) / 2;
    return { left: 0, right: 0, top: end, bottom: end };
  }
  // Image taller than viewport → bars on left/right.
  const used = img / view;
  const side = (1 - used) / 2;
  return { left: side, right: side, top: 0, bottom: 0 };
}

/**
 * Map a point in contain-fitted image space into viewport space.
 */
export function containImagePointToViewport(
  imageX: number,
  imageY: number,
  imageAspect: number,
  viewportAspect: number,
): { x: number; y: number } {
  const insets = containLetterboxInsets(imageAspect, viewportAspect);
  const usedW = 1 - insets.left - insets.right;
  const usedH = 1 - insets.top - insets.bottom;
  return {
    x: insets.left + imageX * usedW,
    y: insets.top + imageY * usedH,
  };
}

/**
 * Map a size in contain-fitted image space into viewport fractions.
 */
export function containImageSizeToViewport(
  imageWidth: number,
  imageHeight: number,
  imageAspect: number,
  viewportAspect: number,
): { width: number; height: number } {
  const insets = containLetterboxInsets(imageAspect, viewportAspect);
  const usedW = Math.max(0.01, 1 - insets.left - insets.right);
  const usedH = Math.max(0.01, 1 - insets.top - insets.bottom);
  return {
    width: imageWidth * usedW,
    height: imageHeight * usedH,
  };
}

/**
 * Phone-landscape playfield? Shared by camera framing helpers.
 */
export function isPhoneLandscapePlayfield(
  playfieldWidth: number,
  playfieldHeight: number,
  config: Partial<ResponsiveExploreConfig> = {},
): boolean {
  const merged = { ...DEFAULT_RESPONSIVE_EXPLORE, ...config };
  const w = playfieldWidth;
  const h = playfieldHeight;
  if (w <= h) return false;
  const isDesktop =
    w >= merged.desktopFitBreakpoint && h >= merged.desktopFitMinHeight;
  if (isDesktop) return false;
  const shortest = Math.min(w, h);
  const isPhone =
    w < merged.tabletBreakpoint || shortest < 500;
  return isPhone;
}

/**
 * Width of the landscape bleed plate relative to the logical world layer.
 * Bleed height matches the world; width scales by ultraAspect / worldAspect
 * so the centered master composition stays pixel-aligned.
 */
export function landscapeBleedWidthFactor(
  worldAspectRatio: number,
  bleedAspectRatio: number,
): number {
  const world = Math.max(0.01, worldAspectRatio);
  const bleed = Math.max(world, bleedAspectRatio);
  return bleed / world;
}

/**
 * World is sized as a multiple of the playfield, so viewport cancels:
 *   worldPx = worldFactor × playfieldPx
 *   fit = playfieldPx / worldPx × (1 − padding) = (1 − padding) / worldFactor
 */
export function computeFitZoom(
  worldWidth: number,
  worldHeight: number,
  padding: number,
): number {
  const safeW = Math.max(0.01, worldWidth);
  const safeH = Math.max(0.01, worldHeight);
  const factor = 1 - clamp(padding, 0, 0.25);
  return Math.min(factor / safeW, factor / safeH);
}

/**
 * Normalized rectangle on the master plate (0–1) that must stay on-screen.
 */
export interface PlayAreaRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Fit zoom so the entire playArea is visible (contain of the fenced yard).
 * Artwork outside the playArea may extend past the viewport and be cropped.
 *
 *   zoom = min(pad / (worldWidth × areaW), pad / (worldHeight × areaH))
 */
export function computePlayAreaFit(
  worldWidth: number,
  worldHeight: number,
  playArea: PlayAreaRect,
  padding = 0,
): {
  zoom: number;
  centerX: number;
  centerY: number;
  areaW: number;
  areaH: number;
} {
  const areaW = Math.max(0.01, playArea.right - playArea.left);
  const areaH = Math.max(0.01, playArea.bottom - playArea.top);
  const pad = 1 - clamp(padding, 0, 0.25);
  const zoom = Math.min(
    pad / (Math.max(0.01, worldWidth) * areaW),
    pad / (Math.max(0.01, worldHeight) * areaH),
  );
  return {
    zoom,
    centerX: (playArea.left + playArea.right) / 2,
    centerY: (playArea.top + playArea.bottom) / 2,
    areaW,
    areaH,
  };
}

/**
 * Mobile backyard: world-cover × overscan so cream shell never peeks, with the
 * resting crop centered on the fenced playArea (clamped to keep coverage).
 * Aspect preserved; no bleed. Desktop play-area path untouched.
 */
export const MOBILE_BACKYARD_COVER_OVERSCAN = 1.04;

export function resolveMobileBackyardExploreZoom(
  input: ExploreZoomInput & { playArea: PlayAreaRect },
): ExploreZoomResult {
  const config: ResponsiveExploreConfig = {
    ...DEFAULT_RESPONSIVE_EXPLORE,
    ...input.config,
  };
  const base = resolveExploreZoom(input);

  // Landscape phones keep near-cover + patio bias from resolveExploreZoom.
  const w = input.playfieldWidth;
  const h = input.playfieldHeight;
  if (w > h && base.frameCenter) {
    return base;
  }

  const coverZoom = computeCoverZoom(input.worldWidth, input.worldHeight, 0);
  const zoom = clamp(
    coverZoom * MOBILE_BACKYARD_COVER_OVERSCAN,
    config.minimumExplorationZoom,
    config.maximumCoverZoom,
  );

  const halfH = Math.min(0.5, 0.5 / (Math.max(0.01, input.worldHeight) * zoom));
  const halfW = Math.min(0.5, 0.5 / (Math.max(0.01, input.worldWidth) * zoom));
  const centerX = clamp(
    (input.playArea.left + input.playArea.right) / 2,
    halfW,
    1 - halfW,
  );
  const centerY = clamp(
    (input.playArea.top + input.playArea.bottom) / 2,
    halfH,
    1 - halfH,
  );

  return {
    zoom,
    // Fence-centered resting frame (same lock mechanism as landscape patio
    // bias). Follow dead-zone stays off so the crop does not drift and reopen
    // cream gutters at the edges.
    mode: 'fit',
    fitZoom: base.fitZoom,
    coverZoom,
    focusZoom: zoom * config.focusZoomRelative,
    frameCenter: { x: centerX, y: centerY },
    config,
  };
}

/**
 * Single desktop backyard projection — used on initial load and every resize.
 * Prefer playArea contain; cover-bump when that would letterbox; frame center
 * stays inside the CSS-pan safe range so height-only resizes never expose
 * cream gutters.
 *
 * Prefer `frameSceneWorld` for backyard locations — that path keeps the
 * SceneWorld pixel box fixed and only updates the camera.
 */
export function calculateDesktopBackyardLayout(input: {
  playfieldWidth: number;
  playfieldHeight: number;
  worldWidth: number;
  worldHeight: number;
  playArea: PlayAreaRect;
  config?: Partial<ResponsiveExploreConfig>;
}): ExploreZoomResult {
  return resolvePlayAreaExploreZoom(input);
}

/**
 * Desktop backyard: fit the fenced playArea, then ensure the playfield is
 * filled (no letterbox gutters). When contain would leave empty bands,
 * bump zoom to world-cover so sky/trees outside the fence crop instead.
 * Skips the generic `maximumExplorationZoom` (0.84) clamp.
 * Mobile backyard does NOT use this — see `resolveMobileBackyardExploreZoom`.
 */
export function resolvePlayAreaExploreZoom(
  input: ExploreZoomInput & { playArea: PlayAreaRect },
): ExploreZoomResult {
  const config: ResponsiveExploreConfig = {
    ...DEFAULT_RESPONSIVE_EXPLORE,
    ...input.config,
  };
  const playPad =
    input.config?.desktopFitPadding != null
      ? clamp(config.desktopFitPadding, 0, 0.25)
      : 0;

  const fitted = computePlayAreaFit(
    input.worldWidth,
    input.worldHeight,
    input.playArea,
    playPad,
  );
  const coverZoom = computeCoverZoom(input.worldWidth, input.worldHeight, 0);

  // Prefer playArea contain. If that letterboxes the playfield (common after
  // resizing away from the master aspect), bump to cover so the viewport
  // stays filled — cropping decorative sky/trees, not inventing bleed gutters.
  let targetZoom = fitted.zoom;
  const renderedW = Math.max(0.01, input.worldWidth) * targetZoom;
  const renderedH = Math.max(0.01, input.worldHeight) * targetZoom;
  if (renderedW < 1 - 1e-4 || renderedH < 1 - 1e-4) {
    targetZoom = Math.max(targetZoom, coverZoom);
  }

  const zoom = clamp(
    targetZoom,
    config.minimumExplorationZoom,
    Math.max(config.maximumCoverZoom, targetZoom),
  );

  // Frame center must stay inside the CSS-pan safe range. The naive
  // half-extent clamp (0.5/cover) is too wide for translate(%)*scale and
  // overshifts short/wide viewports — exposing cream below the yard when
  // only the browser height shrinks.
  const xRange = coverSafeCenterRange(input.worldWidth, zoom);
  const yRange = coverSafeCenterRange(input.worldHeight, zoom);
  const centerX = clamp(fitted.centerX, xRange.min, xRange.max);
  const centerY = clamp(fitted.centerY, yRange.min, yRange.max);

  return {
    zoom,
    mode: 'fit',
    fitZoom: fitted.zoom,
    coverZoom,
    focusZoom: zoom * config.focusZoomRelative,
    frameCenter: { x: centerX, y: centerY },
    config,
  };
}

/**
 * Cover zoom — playfield fully filled, world cropped on the longer axis.
 *   cover = max((1 − padding) / worldWidth, (1 − padding) / worldHeight)
 */
export function computeCoverZoom(
  worldWidth: number,
  worldHeight: number,
  padding: number,
): number {
  const safeW = Math.max(0.01, worldWidth);
  const safeH = Math.max(0.01, worldHeight);
  const factor = 1 - clamp(padding, 0, 0.25);
  return Math.max(factor / safeW, factor / safeH);
}

/**
 * Half-extents of the world visible in the playfield at the given zoom.
 * Used to clamp camera centers so empty space outside the artwork is never shown.
 */
export function visibleWorldHalfExtents(
  zoom: number,
  worldWidth: number,
  worldHeight: number,
): { halfW: number; halfH: number } {
  const z = Math.max(0.01, zoom);
  const halfW = Math.min(0.5, 0.5 / (Math.max(0.01, worldWidth) * z));
  const halfH = Math.min(0.5, 0.5 / (Math.max(0.01, worldHeight) * z));
  return { halfW, halfH };
}

/** Pan strength while exploring — fit mode keeps the full yard framed. */
export function explorePanFactor(mode: ExplorationCameraMode): number {
  switch (mode) {
    case 'fit':
      return 0;
    case 'balanced':
      return 0.4;
    case 'follow':
    default:
      return 1;
  }
}

/**
 * CSS `%` translate offset for a camera center under `scale(zoom)`.
 *
 * Percentages are resolved against the *unscaled* layer, so cover/follow
 * must multiply by zoom or the world under-pans by ~zoom× (portrait phones
 * often cover at 3–4× and barely slide left/right).
 */
export function cameraPanTranslatePercent(
  center: number,
  worldSpan: number,
  zoom: number,
  panFactor: number,
): number {
  return (0.5 - center) * worldSpan * 100 * panFactor * Math.max(0.01, zoom);
}

/**
 * Clamp a CSS translate(%) so a world that *can* cover the playfield never
 * exposes empty bands. Matches `transform: translate(...) scale(zoom)` where
 * translate % is resolved against the unscaled layer.
 *
 * Max |offset| from center = 50*zoom − 50/worldSpan when worldSpan*zoom ≥ 1.
 */
export function clampCoverPanOffsetPercent(
  offsetPercent: number,
  worldSpan: number,
  zoom: number,
): number {
  const span = Math.max(0.01, worldSpan);
  const z = Math.max(0.01, zoom);
  if (span * z < 1 - 1e-4) return offsetPercent;
  const flush = 50 * z - 50 / span;
  return clamp(offsetPercent, -flush, flush);
}

/**
 * Safe camera-center range for the CSS pan model above (not the naive
 * 0.5/cover half-extent). Used by desktop play-area framing.
 */
export function coverSafeCenterRange(
  worldSpan: number,
  zoom: number,
): { min: number; max: number } {
  const span = Math.max(0.01, worldSpan);
  const z = Math.max(0.01, zoom);
  if (span * z < 1 - 1e-4) {
    return { min: 0.5, max: 0.5 };
  }
  const maxDelta = 0.5 / span - 0.5 / (span * span * z);
  return { min: 0.5 - maxDelta, max: 0.5 + maxDelta };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
